import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import jsQR from 'jsqr';
import styles from './OperatorScanModal.module.scss';
import { ScanLine, Truck, CircleCheck, CircleAlert, TriangleAlert } from 'lucide-react';
import { Vehicle } from 'components/Erp/Display';
import { checkOperatorReceiptLocation, getOperatorReceiptError, unavailableOperatorLocation } from '../utils/operatorReceiptGeo';
import { formatBranchDistances } from '../utils/geo';

/**
 * Модальный сканер для операторов баз.
 *
 * Шаги:
 *  idle         — плашки (QR + транспорт), кнопка заблокирована
 *  scanning_qr  — живая камера (задняя), авто-сканирование QR
 *  loading      — запрос к бэку
 *  data         — данные водителя + плашка фото транспорта
 *  done         — успех
 *  error        — ошибка + кнопка «Назад»
 *  warning      — предупреждение + кнопка «Назад»
 */
const OperatorScanModal = ({ onClose, onQRScanned, onConfirm, ensureGeo }) => {
  useInterfaceLanguage();
  const [step, setStep] = useState('idle');
  const [driverData, setDriverData] = useState(null);
  const [lastLoadA, setLastLoadA] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');
  const [transportImageSrc, setTransportImageSrc] = useState(null);
  const [transportBlob, setTransportBlob] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [location, setLocation] = useState({ reason: 'idle', isInRange: false, message: '', distanceInfo: '' });
  const geoRequest = useRef(0);
  const geoChecking = useRef(false);
  const sendPending = useRef(false);
  const ensureGeoRef = useRef(ensureGeo);
  ensureGeoRef.current = ensureGeo;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const cameraRequest = useRef(0);
  const scanningRef = useRef(false);
  const rafRef = useRef(null);
  const photoInputRef = useRef(null);
  // Храним обработчик QR в ref, чтобы не было circular-зависимостей в useCallback
  const onQrDetectedRef = useRef(null);

  // ── Остановка камеры ─────────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    cameraRequest.current += 1;
    scanningRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.srcObject = null; }
  }, []);

  // ── Запуск задней камеры ─────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    const token = ++cameraRequest.current;
    setCameraError('');
    let stream = null;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch (_) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
        } catch (_2) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
      }

      if (token !== cameraRequest.current) { stream.getTracks().forEach(track => track.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', true);
        await videoRef.current.play();
      }

      // Петля сканирования
      scanningRef.current = true;
      const loop = () => {
        if (!scanningRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.readyState < video.HAVE_ENOUGH_DATA) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'attemptBoth' });
        if (code?.data) {
          // QR найден — останавливаем камеру и обрабатываем
          onQrDetectedRef.current?.(code.data.trim());
          return;
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      if (token !== cameraRequest.current) return;
      const msg = err.name === 'NotAllowedError'
        ? tr("Доступ к камере запрещён. Разрешите в настройках браузера.")
        : tr("Не удалось открыть камеру. Попробуйте снова.");
      setCameraError(msg);
      setStep('error');
    }
  }, []);

  // ── Обработчик найденного QR ─────────────────────────────────────────────
  const handleQrDetected = useCallback(async (uid) => {
    stopCamera();
    setStep('loading');
    try {
      const result = await onQRScanned(uid);
      if (result.error) { setErrorMsg(result.error); setStep('error'); }
      else if (result.warning) { setWarningMsg(result.warning); setStep('warning'); }
      else { setDriverData(result.driverData); setLastLoadA(result.lastLoadA); setStep('data'); }
    } catch {
      setErrorMsg(tr("Ошибка загрузки данных водителя. Попробуйте снова."));
      setStep('error');
    }
  }, [stopCamera, onQRScanned]);

  // Обновляем ref при изменении колбэка (избегаем stale closure в петле)
  onQrDetectedRef.current = handleQrDetected;

  // Запускаем/останавливаем камеру вместе с шагом scanning_qr
  useEffect(() => {
    if (step === 'scanning_qr') startCamera();
    else stopCamera();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Останавливаем при unmount
  useEffect(() => () => { geoRequest.current++; stopCamera(); }, [stopCamera]);

  // ── Фото транспорта ───────────────────────────────────────────────────────
  const handlePhotoFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTransportImageSrc(URL.createObjectURL(file));
    setTransportBlob(file);
    e.target.value = '';
  }, []);

  useEffect(() => { if (!transportImageSrc) return; return () => URL.revokeObjectURL(transportImageSrc); }, [transportImageSrc]);

  const checkLocation = useCallback(async () => {
    if (!lastLoadA || geoChecking.current) return null;
    const ticket = ++geoRequest.current;
    geoChecking.current = true;
    setLocation({ reason: 'checking', isInRange: false, message: '', distanceInfo: '' });
    setSendError('');
    try {
      const geo = await ensureGeoRef.current({ fresh: true });
      const checked = checkOperatorReceiptLocation(geo, lastLoadA);
      if (ticket !== geoRequest.current) return null;
      setLocation(checked);
      return { geo, location: checked };
    } catch {
      if (ticket === geoRequest.current) setLocation({ reason: 'unavailable', isInRange: false, message: unavailableOperatorLocation(), distanceInfo: '' });
      return null;
    } finally {
      if (ticket === geoRequest.current) geoChecking.current = false;
    }
  }, [lastLoadA]);

  useEffect(() => {
    if (lastLoadA) checkLocation();
  }, [lastLoadA, checkLocation]);

  // ── Отправка ─────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!transportBlob || !lastLoadA?.id || sendPending.current || geoChecking.current) return;
    sendPending.current = true;
    setSending(true);
    setSendError('');
    try {
      const checked = await checkLocation();
      if (!checked?.location.isInRange) return;
      await onConfirm({ loadId: lastLoadA.id, lastLoadA, driverData, photoBlob: transportBlob, geo: checked.geo });
      setStep('done');
    } catch (err) {
      setSendError(getOperatorReceiptError(err));
      setLocation(previous => ({ ...previous, reason: 'rejected', isInRange: false }));
    } finally {
      sendPending.current = false;
      setSending(false);
    }
  }, [transportBlob, lastLoadA, driverData, checkLocation, onConfirm]);

  // ── Сброс ─────────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    stopCamera();
    setStep('idle');
    setDriverData(null); setLastLoadA(null);
    setErrorMsg(''); setWarningMsg('');
    setTransportImageSrc(null); setTransportBlob(null);
    setSendError(''); setCameraError('');
    geoRequest.current++;
    geoChecking.current = false;
    setLocation({ reason: 'idle', isInRange: false, message: '', distanceInfo: '' });
  }, [stopCamera]);

  const handleClose = useCallback(() => { handleReset(); onClose(); }, [handleReset, onClose]);

  const isStep1Done = ['data', 'sending', 'done'].includes(step);
  const isStep2Done = ['sending', 'done'].includes(step);
  const isStep3Done = step === 'done';

  // ── Рендер ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${step === 'scanning_qr' ? styles.modalFullscreen : ''}`} role="dialog" aria-modal="true" aria-label={tr("Подтверждение прибытия")}>

        {/* Шапка */}
        <div className={styles.header}>
          <span className={styles.title}>{tr("Отметка прибытия")}</span>
          <button className={styles.closeBtn} aria-label={tr("Закрыть сканер")} disabled={sending} onClick={handleClose}>✕</button>
        </div>

        {/* Шаги (скрываем во время сканирования) */}
        {tr(step !== 'scanning_qr' && (
          <>
            <div className={styles.steps}>
              <StepDot num={1} active={step === 'idle' || step === 'loading'} done={isStep1Done} />
              <div className={styles.stepLine} />
              <StepDot num={2} active={step === 'data'} done={isStep2Done} />
              <div className={styles.stepLine} />
              <StepDot num={3} active={step === 'sending'} done={isStep3Done} />
            </div>
            <div className={styles.stepLabels}>
              <span>{tr("Сканировать QR")}</span>
              <span>{tr("Фото транспорта")}</span>
              <span>{tr("Подтвердить")}</span>
            </div>
          </>
        ))}

        {/* ── Живая камера QR ─────────────────────────────────────────────── */}
        {tr(step === 'scanning_qr' && (
          <div className={styles.scannerWrap}>
            <video ref={videoRef} className={styles.scannerVideo} playsInline muted autoPlay />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className={styles.scannerOverlay}>
              <div className={styles.scannerFrame}>
                <span className={styles.corner} />
                <span className={styles.corner} />
                <span className={styles.corner} />
                <span className={styles.corner} />
              </div>
              <p className={styles.scanHint}>{tr("Наведите на QR-код — распознавание автоматическое")}</p>
            </div>
            <button className={styles.scanBackBtn} onClick={() => { stopCamera(); setStep('idle'); }}>{tr("← Назад")}</button>
          </div>
        ))}

        {/* ── Основное тело ───────────────────────────────────────────────── */}
        {tr(step !== 'scanning_qr' && (
          <div className={styles.body}>

            {/* Idle: плашки */}
            {tr(step === 'idle' && (
              <div className={styles.cards}>
                <div className={`${styles.card} ${styles.cardActive}`} role="button" tabIndex={0} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') setStep('scanning_qr'); }} onClick={() => setStep('scanning_qr')}>
                  <span className={styles.cardIcon}><ScanLine size={24} /></span>
                  <span className={styles.cardLabel}>{tr("Нажмите для сканирования QR")}</span>
                  <span className={styles.cardSub}>{tr("Откроется камера")}</span>
                </div>
                <div className={`${styles.card} ${styles.cardDisabled}`}>
                  <span className={styles.cardIcon}><Truck size={24} /></span>
                  <span className={styles.cardLabel}>{tr("Фото транспорта")}</span>
                  <span className={styles.cardSub}>{tr("Сначала сканируйте QR")}</span>
                </div>
                <button className={styles.confirmBtn} disabled>{tr("Подтвердить прибытие")}</button>
              </div>
            ))}

            {/* Loading */}
            {tr(step === 'loading' && (
              <div className={styles.infoBlock}>
                <div className={styles.spinner} />
                <p className={styles.infoText}>{tr("Загрузка данных водителя...")}</p>
              </div>
            ))}

            {/* Error */}
            {tr(step === 'error' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}><CircleAlert size={24} /></span>
                <p className={styles.infoText}>{tr(errorMsg || cameraError)}</p>
                <div className={styles.btnRow}>
                  <button className={styles.backBtn} onClick={handleReset}>{tr("← Назад")}</button>
                </div>
              </div>
            ))}

            {/* Warning */}
            {tr(step === 'warning' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}><TriangleAlert size={24} /></span>
                <p className={styles.infoText}>{tr(warningMsg)}</p>
                <div className={styles.btnRow}>
                  <button className={styles.backBtn} onClick={handleReset}>{tr("← Назад")}</button>
                </div>
              </div>
            ))}

            {/* Data + transport photo */}
            {tr(step === 'data' && driverData && (
              <div className={styles.cards}>
                {/* Данные водителя */}
                <div className={`${styles.card} ${styles.cardDone} ${styles.cardData}`}>
                  <span className={styles.cardIcon}><CircleCheck size={24} /></span>
                  <div className={styles.driverInfo}>
                    <div className={styles.driverRow}><strong>{tr("Водитель:")}</strong> {tr(driverData.employee_name || '-')}</div>
                    <div className={styles.driverRow}><strong>{tr("Телефон:")}</strong> {tr(driverData.employee_phone || '-')}</div>
                    <div className={styles.driverRow}><strong>{tr("Машина:")}</strong> <Vehicle number={driverData.vehicle_number} model={driverData.vehicle_model} /></div>
                    <div className={styles.driverRow}><strong>{tr("База:")}</strong> {tr(driverData.top_base_name || driverData.chpp_name || driverData.private_point_name || '-')}</div>
                    {tr(lastLoadA && <div className={styles.driverRow}><strong>{tr("Погрузка №:")}</strong> {tr(lastLoadA.id)}</div>)}
                  </div>
                </div>

                {/* Фото транспорта */}
                <div
                  className={`${styles.card} ${transportImageSrc ? styles.cardDone : styles.cardActive}`}
                  role="button" tabIndex={sending ? -1 : 0} aria-label={tr("Фото транспорта")} aria-disabled={sending}
                  onKeyDown={event => { if (!sending && (event.key === 'Enter' || event.key === ' ')) photoInputRef.current?.click(); }}
                  onClick={() => { if (!sending) photoInputRef.current?.click(); }}
                >
                  {transportImageSrc ? (
                    <>
                      <img src={transportImageSrc} alt={tr("Транспорт")} className={styles.cardThumb} />
                      <span className={styles.cardLabel}>{tr("Фото сделано ✓")}</span>
                      <span className={styles.cardSub}>{tr("Нажмите для пересъёмки")}</span>
                    </>
                  ) : (
                    <>
                      <span className={styles.cardIcon}><Truck size={24} /></span>
                      <span className={styles.cardLabel}>{tr("Нажмите, чтобы")}<br />{tr("сфотографировать транспорт")}</span>
                    </>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    disabled={sending}
                    onChange={handlePhotoFileChange}
                  />
                </div>

                <div className={`${styles.locationCheck} ${location.isInRange ? styles.locationInside : styles.locationWarning}`} aria-live="polite" aria-busy={location.reason === 'checking'}>
                  {location.reason === 'checking' ? <p>{tr('Проверяем местоположение…')}</p> : <>
                    {location.reason === 'outside' && <strong><TriangleAlert size={18} aria-hidden="true" />{tr('Оператор вне зоны действия')}</strong>}
                    {location.message && location.reason !== 'rejected' && <p>{tr(location.message)}</p>}
                    {location.distances?.length > 0 && location.reason !== 'rejected' && <p>{tr('Ближайший филиал: ')}{formatBranchDistances(location.distances)}</p>}
                  </>}
                  <button type="button" className={styles.backBtn} disabled={sending || location.reason === 'checking'} onClick={checkLocation}>{tr('Проверить местоположение')}</button>
                </div>

                {sendError && <p role="alert" className={styles.sendError}>{tr(sendError)}</p>}

                <button
                  className={styles.confirmBtn}
                  disabled={!transportBlob || sending || !location.isInRange}
                  onClick={handleConfirm}
                >
                  {sending ? tr("Отправка...") : tr("Подтвердить прибытие")}
                </button>
              </div>
            ))}

            {/* Done */}
            {tr(step === 'done' && (
              <div className={styles.infoBlock}>
                <span className={styles.infoIcon}><CircleCheck size={24} /></span>
                <p className={styles.infoText}>{tr("Прибытие успешно подтверждено!")}</p>
                <div className={styles.btnRow}>
                  <button className={styles.confirmBtn} onClick={handleClose}>{tr("Закрыть")}</button>
                </div>
              </div>
            ))}

          </div>
        ))}

      </div>
    </div>
  );
};

const StepDot = ({ num, active, done }) => { useInterfaceLanguage(); return (<div style={{
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 13, flexShrink: 0, transition: 'all 0.2s',
    background: done ? "var(--theme-success, #238268)" : active ? "var(--theme-primary, #0d00f3)" : "var(--theme-surface-muted, #e5e9ef)",
    color: (done || active) ? '#fff' : "var(--theme-text-muted, #6c757d)",
  }}>
    {done ? '✓' : num}
  </div>); };

export default React.memo(OperatorScanModal);
